// Single entry point. Owns the Board/Resident role, both controllers, and the
// choice of shell. Switching role or viewport never loses state.

import React, { useCallback, useEffect, useState } from "react";
import { useBoardIQ } from "./useBoardIQ.js";
import { useResidentIQ } from "./useResidentIQ.js";
import { useTheme, useDebugConsole } from "./theme.js";
import { useSession } from "./session.js";
import MobileApp from "./MobileApp.jsx";
import DesktopApp from "./DesktopApp.jsx";
import "./tokens.css";

const DESKTOP_MIN = 900;
// Exported so CommunityPicker.jsx can persist the role that the login
// credential (admin vs. a resident's email, see api.js's selectCommunity)
// resolved to, before this component ever mounts.
export const ROLE_KEY = "hoa-role";

export function useIsDesktop(min = DESKTOP_MIN) {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: " + min + "px)").matches : true
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: " + min + "px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [min]);
  return isDesktop;
}

/**
 * @param {"auto"|"mobile"|"desktop"} view  — force a shell, or let the viewport decide.
 * @param {"board"|"resident"} defaultRole  — initial view when nothing is remembered.
 * @param {boolean} allowRoleSwitch         — hide the switch for a resident-only deployment.
 */
export default function BoardIQ({ view = "auto", defaultRole, allowRoleSwitch = true }) {
  const { session, clearSession } = useSession();
  const bq = useBoardIQ();
  // session.communityName is set the instant login succeeds (App.jsx's
  // onSelectCommunity), so ResidentIQ has a real community name from its
  // very first render rather than waiting on bq's own GET /community.
  const riq = useResidentIQ({
    communityName: session.communityName || (bq.community && bq.community.name),
    sessionResident: session.resident,
  });
  const { theme, font, setTheme, setFont } = useTheme("light");
  const { debugOn, toggleDebug } = useDebugConsole();
  const isDesktop = useIsDesktop();

  const [role, setRoleState] = useState(() => {
    if (defaultRole) return defaultRole;
    try { return localStorage.getItem(ROLE_KEY) || "board"; } catch { return "board"; }
  });

  const setRole = useCallback((next) => {
    setRoleState(next);
    try { localStorage.setItem(ROLE_KEY, next); } catch { /* private mode */ }
    window.dispatchEvent(new CustomEvent("hoa:role-change", { detail: next }));
  }, []);

  // Sign out: clears every local cache this app writes (role, theme/font,
  // debug-console flag, the resident's saved identity/managers -- see
  // theme.js and useResidentIQ.js for the individual keys) and does a hard
  // navigation to "/" rather than a hash change, so nothing in memory
  // (chat threads, the resident/board hooks' state, any open dialog)
  // survives into the next session -- the landing page gets a real reload,
  // not just a re-render. There's no server-side session token to revoke:
  // /communities/select just swaps which community is active, so signing
  // out is entirely a client-side reset.
  const signOut = useCallback(() => {
    clearSession();
    try { localStorage.clear(); } catch { /* private mode */ }
    window.location.href = "/";
  }, [clearSession]);

  const Shell = view === "mobile" ? MobileApp : view === "desktop" ? DesktopApp : isDesktop ? DesktopApp : MobileApp;

  return (
    <Shell
      bq={bq}
      riq={riq}
      session={session}
      role={role}
      setRole={setRole}
      allowRoleSwitch={allowRoleSwitch}
      theme={theme}
      font={font}
      setTheme={setTheme}
      setFont={setFont}
      debugOn={debugOn}
      toggleDebug={toggleDebug}
      onSignOut={signOut}
    />
  );
}

export { MobileApp, DesktopApp };
